import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

type Language = 'ru' | 'en' | 'zh';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Translations
const translations: Record<Language, Record<string, string>> = {
  ru: {
    // Navigation
    'nav.services': 'Услуги',
    'nav.prices': 'Цены',
    'nav.order': 'Заказать доставку',
    'nav.about': 'О нас',
    'nav.contacts': 'Контакты',
    'nav.login': 'Войти',
    'nav.dashboard': 'Личный кабинет',
    'nav.language': 'Язык сайта',
    'nav.russian': 'Русский',
    'nav.english': 'English',
    'nav.chinese': '中文',

    // Services submenu
    'services.search': 'Поиск и выкуп товаров',
    'services.logistics': 'Логистика и таможня',
    'services.inspection': 'Инспекционный контроль',
    'services.payment': 'Оплата поставщикам',

    // Hero section
    'hero.title': 'Доставка из Китая быстро и надёжно',
    'hero.subtitle': 'Привозим товары из Китая под ключ: от поиска до таможни. Без задержек, по лучшей цене.',
    'hero.stats.experience': 'лет опыта',
    'hero.stats.clients': 'клиентов',
    'hero.stats.success': 'успешных сделок',
    'hero.cta.order': 'Заказать доставку',
    'hero.cta.calculate': 'Рассчитать стоимость',

    // Logistics slide
    'logistics.title': 'Логистика и таможенное',
    'logistics.subtitle': 'оформление грузов',
    'logistics.description': 'Организуем доставку любых грузов из Китая с полным таможенным оформлением',
    'logistics.stats.delivery': 'дней доставка',
    'logistics.stats.tracking': 'отслеживание',
    'logistics.stats.insurance': 'страховка',
    'logistics.cta': 'Подробнее об услуге',

    // Inspection slide
    'inspection.title': 'Инспекционный контроль',
    'inspection.subtitle': 'качества продукции',
    'inspection.description': 'Проверяем качество товаров на производстве до отправки. Фото и видео отчеты',
    'inspection.stats.check': 'дня проверка',
    'inspection.stats.parameters': 'параметров',
    'inspection.stats.defects': 'брака',
    'inspection.cta': 'Подробнее об услуге',

    // Payment slide
    'payment.title': 'Безопасная оплата',
    'payment.subtitle': 'китайским поставщикам',
    'payment.description': 'Проводим платежи в Китай без рисков. Защита сделок и возврат платежей',
    'payment.stats.transfer': 'дня перевод',
    'payment.stats.commission': 'комиссия',
    'payment.stats.guarantee': 'гарантия',
    'payment.cta': 'Подробнее об услуге',

    // Search slide
    'search.title': 'Поиск и выкуп товаров',
    'search.subtitle': 'напрямую с фабрик Китая',
    'search.description': 'Найдем надежных производителей, проверим качество и организуем поставки точно в срок',
    'search.stats.search': 'Поиск',
    'search.stats.search.desc': 'Подбор поставщиков по вашим критериям',
    'search.stats.check': 'Проверка',
    'search.stats.check.desc': 'Контроль качества на производстве',
    'search.stats.delivery': 'Доставка',
    'search.stats.delivery.desc': 'Быстрая и надежная логистика',
    'search.cta': 'Подробнее об услуге',

    // Advantages section
    'advantages.title': 'Наши преимущества',
    'advantages.logistics.title': 'Полная логистика',
    'advantages.logistics.desc': 'Доставка от китайского склада до вашей двери с оформлением.',
    'advantages.security.title': 'Гарантия безопасности',
    'advantages.security.desc': 'Страховка и контроль груза на каждом этапе.',
    'advantages.speed.title': 'Быстрая доставка',
    'advantages.speed.desc': 'Сроки — от 10 дней по оптимальному маршруту.',

    // Business solutions
    'business.title': 'Решения для бизнеса',
    'business.subtitle': 'Комплексный подход к работе с китайскими поставщиками для компаний любого масштаба',
    'business.large.title': 'Для крупного бизнеса',
    'business.large.features': [
      'Персональный менеджер 24/7',
      'Прямые контракты с фабриками',
      'Особые условия доставки',
      'Таможенное оформление'
    ],
    'business.small.title': 'Для малого бизнеса',
    'business.small.features': [
      'Поиск надежных поставщиков',
      'Выкуп товаров',
      'Консолидация грузов',
      'Оптимальные тарифы'
    ],

    // Services section
    'services.title': 'Наши услуги',
    'services.cargo.title': 'ГРУЗОВЫЕ ПЕРЕВОЗКИ',
    'services.cargo.desc': 'Перевозим любые типы грузов — от сборных до негабаритных и проектных поставок.',
    'services.cargo.timing': 'Сроки: индивидуально',
    'services.currency.title': 'ОБМЕН ВАЛЮТЫ',
    'services.currency.desc': 'Проводим безопасные трансферы любой валюты без комиссии и скрытых условий.',
    'services.currency.timing': 'От 5 дней',
    'services.translator.title': 'УСЛУГИ ПЕРЕВОДЧИКА',
    'services.translator.desc': 'Предоставим профессионального переводчика для деловых переговоров и сопровождения.',
    'services.translator.timing': 'От 1 дня',
    'services.customs.title': 'ТАМОЖЕННОЕ СОПРОВОЖДЕНИЕ',
    'services.customs.desc': 'Оформим груз по всем правилам: от границы до склада. Быстро, прозрачно, легально.',
    'services.customs.timing': 'От 7 дней',
    'services.suppliers.title': 'ПОИСК ПОСТАВЩИКОВ',
    'services.suppliers.desc': 'Найдём надёжных производителей, проверим товары и организуем поставки.',
    'services.suppliers.timing': 'От 1 дня',
    'services.quality.title': 'ПРОВЕРКА КАЧЕСТВА',
    'services.quality.desc': 'Личный контроль качества на фабрике. Мы на месте — вы спокойны.',
    'services.quality.timing': 'От 7 дней',
    'services.samples.title': 'ДОСТАВКА ОБРАЗЦОВ',
    'services.samples.desc': 'Быстро отправим образцы для оценки качества, чтобы вы не теряли время.',
    'services.samples.timing': 'От 5 дней',
    'services.containers.title': 'СОБСТВЕННЫЕ КОНТЕЙНЕРЫ',
    'services.containers.desc': 'Предоставим личный контейнер для регулярных поставок с приоритетной отправкой.',
    'services.containers.timing': 'От 7 дней',
    'services.manufacturing.title': 'КОНТРАКТНОЕ ПРОИЗВОДСТВО',
    'services.manufacturing.desc': 'Поможем создать товар под вашим брендом: от идеи до производства.',
    'services.manufacturing.timing': 'От 14 дней',
    'services.marketplaces.title': 'ВЫХОД НА МАРКЕТПЛЕЙСЫ',
    'services.marketplaces.desc': 'Проведем через все этапы выхода на площадки: регистрация, листинг, продажи.',
    'services.marketplaces.timing': 'От 7–14 дней',
    'services.storage.title': 'СКЛАДСКОЕ ХРАНЕНИЕ',
    'services.storage.desc': 'Обеспечим хранение ваших товаров на безопасных складах в Китае и РФ.',
    'services.storage.timing': 'От 1 дня',
    'services.competitors.title': 'АНАЛИЗ КОНКУРЕНТОВ',
    'services.competitors.desc': 'Покажем, как и что возят ваши конкуренты — от логистики до таможенных схем.',
    'services.competitors.timing': 'От 2 часов до 1 дня',
    'services.cta': 'ОСТАВИТЬ ЗАЯВКУ',

    // FAQ section
    'faq.title': 'Часто задаваемые вопросы',
    'faq.delivery.question': 'Как организована доставка товаров из Китая?',
    'faq.delivery.answer': 'Доставка осуществляется автомобильным транспортом тремя основными способами:\n\nБыстрое авто — срок доставки 7-8 дней\nЭкспресс-доставка — срок доставки 3 дня\n\nМы подбираем оптимальный способ доставки в зависимости от ваших приоритетов по срокам и бюджету. Все этапы логистики — от получения груза до прибытия — мы берем на себя.',
    'faq.forwarding.question': 'Что такое экспедирование товаров из Китая?',
    'faq.forwarding.answer': 'Экспедирование товаров в Китае — это комплекс мероприятий, направленных на обеспечение безопасной и своевременной отправки груза. В него входят:\n\n• проверка качества и комплектности товаров,\n• фото и видеофиксация,\n• контроль соответствия товара заявке,\n• упаковка и маркировка,\n• консолидация партий,\n• координация с поставщиком и складом.\n\nЭкспедирование позволяет минимизировать риски при доставке и убедиться, что товар соответствует ожиданиям до его отправки.',
    'faq.destination.question': 'Куда приходит груз из Китая?',
    'faq.destination.answer': 'По умолчанию груз прибывает на склад "Южные ворота" в Москве. По согласованию возможна доставка на склады "Люблино" или "Садовод".\n\nЕсли вы заказываете партию от 5 тонн или 30 кубов, доставка по Москве осуществляется бесплатно.',
    'faq.regions.question': 'Как доставляется груз в регионы России?',
    'faq.regions.answer': 'Груз сначала поступает на наш склад в Москве, после чего по согласованию с клиентом передаётся в транспортную компанию для дальнейшей доставки по регионам России.\nСтоимость доставки рассчитывается по тарифам выбранной транспортной компании.',
    'faq.commission.question': 'Сколько стоит комиссия за выкуп и поиск товара?',
    'faq.commission.answer': 'Комиссия зависит от сложности задачи и количества запросов:\n\n• Поиск и выкуп товара — бесплатно.\n• Если товар уже найден и требуется только оплата поставщику — услуга бесплатна.\n• Поиск контактов фабрики — фиксированная стоимость 200 юаней.\n\nМы всегда предлагаем прозрачные условия и обсуждаем стоимость до начала работы.',
    'faq.packaging.question': 'Сколько стоит упаковка?',
    'faq.packaging.answer': 'Упаковка подбирается в зависимости от типа товара, степени хрупкости и маршрута доставки. Ниже представлены основные варианты и стоимость:\n\n• Мешок + Скотч 5$\n• Угол+Мешок+Скотч 6$\n• Лента+Мешок+Скотч 7$\n• Деревянная обрешетка + Мешок + Скотч 10$\n• Палет 45$ куб\n\nПочему упаковка важна: При перевозке груз может промокнуть, попасть под тяжёлые коробки или получить повреждения. Поэтому грамотный выбор упаковки — это защита вашего товара от любых рисков в пути.',
    'faq.cost.question': 'Сколько стоит доставка в Россию?',
    'faq.cost.answer': 'Стоимость доставки зависит от категории товара, его плотности и выбранного типа доставки, поэтому назвать точную цену без уточнений невозможно.\n\nНа странице 👉 Цены мы показали примерную стоимость доставки по одной из популярных категорий (одежда) за 2024 год. Это поможет вам ориентироваться в диапазоне цен.\n\nЧтобы узнать актуальную цену именно для вашего груза — оставьте заявку, и наш менеджер свяжется с вами, уточнит параметры заказа и предоставит персональное коммерческое предложение в течение 5 минут.',
    'faq.prices': 'Цены',
    'faq.request': 'оставьте заявку',

    // Contact form
    'contact.title': 'Получить расчёт стоимости',
    'contact.subtitle': 'Заполните форму, и мы свяжемся с вами для обсуждения деталей',
    'form.product': 'Ссылка на товар или описание',
    'form.product.placeholder': 'Укажите ссылку на товар или опишите, что вы ищете',
    'form.comment': 'Комментарий',
    'form.comment.placeholder': 'Дополнительная информация или пожелания',
    'form.name': 'Имя и фамилия *',
    'form.name.placeholder': 'Иван Иванов',
    'form.phone': 'Телефон *',
    'form.phone.placeholder': '+7 (___) ___-__-__',
    'form.submit': 'Отправить заявку',
    'form.submitting': 'Отправка...',
    'form.success': 'Спасибо! Ваша заявка отправлена. Мы свяжемся с вами в ближайшее время.',
    'form.error.name': 'Пожалуйста, введите имя',
    'form.error.phone': 'Пожалуйста, введите телефон',
    'form.error.phone.invalid': 'Пожалуйста, введите корректный номер телефона',

    // Footer
    'footer.company': 'Ваш надежный партнер в организации поставок из Китая. Комплексные решения для бизнеса и частных лиц.',
    'footer.services': 'Услуги',
    'footer.prices': 'Цены',
    'footer.search': 'Поиск поставщиков',
    'footer.logistics': 'Логистика',
    'footer.forwarding': 'Экспедирование',
    'footer.payment': 'Оплата поставщикам',
    'footer.company.title': 'Компания',
    'footer.about': 'О нас',
    'footer.contacts': 'Контакты',
    'footer.contacts.title': 'Контакты',
    'footer.rights': '© 2025 6069 Cargo. Все права защищены',
    'footer.privacy': 'Политика конфиденциальности',
    'footer.terms': 'Условия использования'
  },
  en: {
    // Navigation
    'nav.services': 'Services',
    'nav.prices': 'Prices',
    'nav.order': 'Order Delivery',
    'nav.about': 'About Us',
    'nav.contacts': 'Contacts',
    'nav.login': 'Login',
    'nav.dashboard': 'Dashboard',
    'nav.language': 'Language',
    'nav.russian': 'Русский',
    'nav.english': 'English',
    'nav.chinese': '中文',

    // Services submenu
    'services.search': 'Product Sourcing',
    'services.logistics': 'Logistics & Customs',
    'services.inspection': 'Quality Inspection',
    'services.payment': 'Payments to Suppliers',

    // Hero section
    'hero.title': 'Fast and Reliable Delivery from China',
    'hero.subtitle': 'We deliver goods from China turnkey: from sourcing to customs clearance. No delays, best prices.',
    'hero.stats.experience': 'years of experience',
    'hero.stats.clients': 'clients',
    'hero.stats.success': 'successful deals',
    'hero.cta.order': 'Order Delivery',
    'hero.cta.calculate': 'Calculate Cost',

    // Logistics slide
    'logistics.title': 'Logistics and Customs',
    'logistics.subtitle': 'Clearance Services',
    'logistics.description': 'We organize delivery of any cargo from China with full customs clearance',
    'logistics.stats.delivery': 'days delivery',
    'logistics.stats.tracking': 'tracking',
    'logistics.stats.insurance': 'insurance',
    'logistics.cta': 'Learn More',

    // Inspection slide
    'inspection.title': 'Quality Inspection',
    'inspection.subtitle': 'Control Services',
    'inspection.description': 'We check the quality of goods at the factory before shipping. Photo and video reports',
    'inspection.stats.check': 'days inspection',
    'inspection.stats.parameters': 'parameters',
    'inspection.stats.defects': 'defects',
    'inspection.cta': 'Learn More',

    // Payment slide
    'payment.title': 'Secure Payments',
    'payment.subtitle': 'to Chinese Suppliers',
    'payment.description': 'We process payments to China without risks. Transaction protection and payment refunds',
    'payment.stats.transfer': 'days transfer',
    'payment.stats.commission': 'commission',
    'payment.stats.guarantee': 'guarantee',
    'payment.cta': 'Learn More',

    // Search slide
    'search.title': 'Product Sourcing',
    'search.subtitle': 'Directly from Chinese Factories',
    'search.description': 'We find reliable manufacturers, check quality and organize deliveries on time',
    'search.stats.search': 'Sourcing',
    'search.stats.search.desc': 'Finding suppliers based on your criteria',
    'search.stats.check': 'Inspection',
    'search.stats.check.desc': 'Quality control at the factory',
    'search.stats.delivery': 'Delivery',
    'search.stats.delivery.desc': 'Fast and reliable logistics',
    'search.cta': 'Learn More',

    // Advantages section
    'advantages.title': 'Our Advantages',
    'advantages.logistics.title': 'Full Logistics',
    'advantages.logistics.desc': 'Delivery from Chinese warehouse to your door with clearance.',
    'advantages.security.title': 'Security Guarantee',
    'advantages.security.desc': 'Insurance and cargo control at every stage.',
    'advantages.speed.title': 'Fast Delivery',
    'advantages.speed.desc': 'Delivery times from 10 days via optimal route.',

    // Business solutions
    'business.title': 'Business Solutions',
    'business.subtitle': 'Comprehensive approach to working with Chinese suppliers for companies of any size',
    'business.large.title': 'For Large Business',
    'business.large.features': [
      '24/7 Personal Manager',
      'Direct Contracts with Factories',
      'Special Delivery Terms',
      'Customs Clearance'
    ],
    'business.small.title': 'For Small Business',
    'business.small.features': [
      'Reliable Supplier Search',
      'Product Purchasing',
      'Cargo Consolidation',
      'Optimal Rates'
    ],

    // Services section
    'services.title': 'Our Services',
    'services.cargo.title': 'CARGO TRANSPORTATION',
    'services.cargo.desc': 'We transport all types of cargo — from consolidated to oversized and project deliveries.',
    'services.cargo.timing': 'Timing: individual',
    'services.currency.title': 'CURRENCY EXCHANGE',
    'services.currency.desc': 'We conduct safe transfers of any currency without commission and hidden conditions.',
    'services.currency.timing': 'From 5 days',
    'services.translator.title': 'TRANSLATOR SERVICES',
    'services.translator.desc': 'We provide a professional translator for business negotiations and support.',
    'services.translator.timing': 'From 1 day',
    'services.customs.title': 'CUSTOMS SUPPORT',
    'services.customs.desc': 'We process cargo according to all rules: from border to warehouse. Fast, transparent, legal.',
    'services.customs.timing': 'From 7 days',
    'services.suppliers.title': 'SUPPLIER SEARCH',
    'services.suppliers.desc': 'We find reliable manufacturers, check products and organize supplies.',
    'services.suppliers.timing': 'From 1 day',
    'services.quality.title': 'QUALITY CONTROL',
    'services.quality.desc': 'Personal quality control at the factory. We are on site — you are calm.',
    'services.quality.timing': 'From 7 days',
    'services.samples.title': 'SAMPLE DELIVERY',
    'services.samples.desc': 'We quickly send samples for quality assessment so you don\'t waste time.',
    'services.samples.timing': 'From 5 days',
    'services.containers.title': 'OWN CONTAINERS',
    'services.containers.desc': 'We provide a personal container for regular deliveries with priority shipping.',
    'services.containers.timing': 'From 7 days',
    'services.manufacturing.title': 'CONTRACT MANUFACTURING',
    'services.manufacturing.desc': 'We help create a product under your brand: from idea to production.',
    'services.manufacturing.timing': 'From 14 days',
    'services.marketplaces.title': 'MARKETPLACE ENTRY',
    'services.marketplaces.desc': 'We guide you through all stages of entering platforms: registration, listing, sales.',
    'services.marketplaces.timing': 'From 7-14 days',
    'services.storage.title': 'WAREHOUSE STORAGE',
    'services.storage.desc': 'We provide storage of your goods in secure warehouses in China and Russia.',
    'services.storage.timing': 'From 1 day',
    'services.competitors.title': 'COMPETITOR ANALYSIS',
    'services.competitors.desc': 'We show how your competitors transport goods — from logistics to customs schemes.',
    'services.competitors.timing': 'From 2 hours to 1 day',
    'services.cta': 'REQUEST A QUOTE',

    // FAQ section
    'faq.title': 'Frequently Asked Questions',
    'faq.delivery.question': 'How is delivery from China organized?',
    'faq.delivery.answer': 'Delivery is carried out by road transport in three main ways:\n\nFast auto — delivery time 7-8 days\nExpress delivery — delivery time 3 days\n\nWe select the optimal delivery method depending on your priorities in terms of timing and budget. We take care of all logistics stages — from receiving cargo to arrival.',
    'faq.forwarding.question': 'What is freight forwarding from China?',
    'faq.forwarding.answer': 'Freight forwarding in China is a set of activities aimed at ensuring safe and timely shipment of cargo. It includes:\n\n• checking the quality and completeness of goods,\n• photo and video recording,\n• control of compliance of goods with the order,\n• packaging and labeling,\n• consolidation of batches,\n• coordination with supplier and warehouse.\n\nForwarding minimizes delivery risks and ensures that the product meets expectations before it is shipped.',
    'faq.destination.question': 'Where does cargo from China arrive?',
    'faq.destination.answer': 'By default, cargo arrives at the "South Gate" warehouse in Moscow. By agreement, delivery to "Lyublino" or "Sadovod" warehouses is possible.\n\nIf you order a batch of 5 tons or 30 cubic meters or more, delivery in Moscow is free.',
    'faq.regions.question': 'How is cargo delivered to regions of Russia?',
    'faq.regions.answer': 'The cargo first arrives at our warehouse in Moscow, after which, in agreement with the client, it is transferred to a transport company for further delivery to the regions of Russia.\nThe cost of delivery is calculated according to the rates of the selected transport company.',
    'faq.commission.question': 'How much is the commission for product purchase and search?',
    'faq.commission.answer': 'The commission depends on the complexity of the task and the number of requests:\n\n• Search and purchase of goods — free.\n• If the product has already been found and only payment to the supplier is required — the service is free.\n• Search for factory contacts — fixed cost of 200 yuan.\n\nWe always offer transparent conditions and discuss the cost before starting work.',
    'faq.packaging.question': 'How much does packaging cost?',
    'faq.packaging.answer': 'Packaging is selected depending on the type of product, degree of fragility and delivery route. Below are the main options and cost:\n\n• Bag + Tape $5\n• Corner+Bag+Tape $6\n• Ribbon+Bag+Tape $7\n• Wooden crate + Bag + Tape $10\n• Pallet $45 per cubic meter\n\nWhy packaging is important: During transportation, cargo can get wet, get under heavy boxes or get damaged. Therefore, the right choice of packaging is the protection of your product from any risks on the way.',
    'faq.cost.question': 'How much does delivery to Russia cost?',
    'faq.cost.answer': 'The cost of delivery depends on the category of goods, their density and the selected type of delivery, so it is impossible to name an exact price without clarification.\n\nOn the 👉 Prices page, we showed the approximate cost of delivery for one of the popular categories (clothing) for 2024. This will help you navigate the price range.\n\nTo find out the current price for your cargo — leave a request, and our manager will contact you, clarify the order parameters and provide a personal commercial offer within 5 minutes.',
    'faq.prices': 'Prices',
    'faq.request': 'leave a request',

    // Contact form
    'contact.title': 'Get a Cost Estimate',
    'contact.subtitle': 'Fill out the form and we will contact you to discuss the details',
    'form.product': 'Product link or description',
    'form.product.placeholder': 'Specify a link to the product or describe what you are looking for',
    'form.comment': 'Comment',
    'form.comment.placeholder': 'Additional information or wishes',
    'form.name': 'Name and surname *',
    'form.name.placeholder': 'John Smith',
    'form.phone': 'Phone *',
    'form.phone.placeholder': '+7 (___) ___-__-__',
    'form.submit': 'Submit Request',
    'form.submitting': 'Submitting...',
    'form.success': 'Thank you! Your request has been sent. We will contact you shortly.',
    'form.error.name': 'Please enter your name',
    'form.error.phone': 'Please enter your phone number',
    'form.error.phone.invalid': 'Please enter a valid phone number',

    // Footer
    'footer.company': 'Your reliable partner in organizing deliveries from China. Comprehensive solutions for businesses and individuals.',
    'footer.services': 'Services',
    'footer.prices': 'Prices',
    'footer.search': 'Supplier Search',
    'footer.logistics': 'Logistics',
    'footer.forwarding': 'Forwarding',
    'footer.payment': 'Payments to Suppliers',
    'footer.company.title': 'Company',
    'footer.about': 'About Us',
    'footer.contacts': 'Contacts',
    'footer.contacts.title': 'Contacts',
    'footer.rights': '© 2025 6069 Cargo. All rights reserved',
    'footer.privacy': 'Privacy Policy',
    'footer.terms': 'Terms of Use'
  },
  zh: {
    // Navigation
    'nav.services': '服务',
    'nav.prices': '价格',
    'nav.order': '下单',
    'nav.about': '关于我们',
    'nav.contacts': '联系方式',
    'nav.login': '登录',
    'nav.dashboard': '个人中心',
    'nav.language': '网站语言',
    'nav.russian': '俄语',
    'nav.english': '英语',
    'nav.chinese': '中文',

    // Services submenu
    'services.search': '产品采购',
    'services.logistics': '物流和海关',
    'services.inspection': '质量检验',
    'services.payment': '供应商付款',

    // Hero section
    'hero.title': '从中国快速可靠的货物运输',
    'hero.subtitle': '我们提供从中国的一站式货物运输：从采购到海关清关。无延迟，最优价格。',
    'hero.stats.experience': '年经验',
    'hero.stats.clients': '客户',
    'hero.stats.success': '成功交易',
    'hero.cta.order': '下单运输',
    'hero.cta.calculate': '计算费用',

    // Logistics slide
    'logistics.title': '物流和海关',
    'logistics.subtitle': '清关服务',
    'logistics.description': '我们组织从中国运输任何货物，提供全面的海关清关服务',
    'logistics.stats.delivery': '天运输',
    'logistics.stats.tracking': '全天候跟踪',
    'logistics.stats.insurance': '保险',
    'logistics.cta': '了解更多',

    // Inspection slide
    'inspection.title': '质量检验',
    'inspection.subtitle': '控制服务',
    'inspection.description': '我们在发货前在工厂检查货物质量。提供照片和视频报告',
    'inspection.stats.check': '天检验',
    'inspection.stats.parameters': '参数',
    'inspection.stats.defects': '缺陷率',
    'inspection.cta': '了解更多',

    // Payment slide
    'payment.title': '安全支付',
    'payment.subtitle': '中国供应商',
    'payment.description': '我们无风险处理对中国的付款。交易保护和付款退款',
    'payment.stats.transfer': '天转账',
    'payment.stats.commission': '佣金',
    'payment.stats.guarantee': '保证',
    'payment.cta': '了解更多',

    // Search slide
    'search.title': '产品采购',
    'search.subtitle': '直接从中国工厂',
    'search.description': '我们寻找可靠的制造商，检查质量并按时组织交付',
    'search.stats.search': '采购',
    'search.stats.search.desc': '根据您的标准寻找供应商',
    'search.stats.check': '检验',
    'search.stats.check.desc': '工厂质量控制',
    'search.stats.delivery': '运输',
    'search.stats.delivery.desc': '快速可靠的物流',
    'search.cta': '了解更多',

    // Advantages section
    'advantages.title': '我们的优势',
    'advantages.logistics.title': '全面物流',
    'advantages.logistics.desc': '从中国仓库到您的门口的交付，包括清关。',
    'advantages.security.title': '安全保障',
    'advantages.security.desc': '每个阶段的保险和货物控制。',
    'advantages.speed.title': '快速交付',
    'advantages.speed.desc': '通过最佳路线10天起的交付时间。',

    // Business solutions
    'business.title': '商业解决方案',
    'business.subtitle': '为任何规模的公司提供与中国供应商合作的综合方法',
    'business.large.title': '大型企业',
    'business.large.features': [
      '24/7个人经理',
      '与工厂直接合同',
      '特殊交付条款',
      '海关清关'
    ],
    'business.small.title': '小型企业',
    'business.small.features': [
      '可靠供应商搜索',
      '产品采购',
      '货物合并',
      '最佳费率'
    ],

    // Services section
    'services.title': '我们的服务',
    'services.cargo.title': '货物运输',
    'services.cargo.desc': '我们运输所有类型的货物 — 从合并到超大尺寸和项目交付。',
    'services.cargo.timing': '时间：个性化',
    'services.currency.title': '货币兑换',
    'services.currency.desc': '我们安全转账任何货币，无佣金和隐藏条件。',
    'services.currency.timing': '5天起',
    'services.translator.title': '翻译服务',
    'services.translator.desc': '我们为商务谈判和支持提供专业翻译。',
    'services.translator.timing': '1天起',
    'services.customs.title': '海关支持',
    'services.customs.desc': '我们按照所有规则处理货物：从边境到仓库。快速，透明，合法。',
    'services.customs.timing': '7天起',
    'services.suppliers.title': '供应商搜索',
    'services.suppliers.desc': '我们寻找可靠的制造商，检查产品并组织供应。',
    'services.suppliers.timing': '1天起',
    'services.quality.title': '质量控制',
    'services.quality.desc': '工厂的个人质量控制。我们在现场 — 您放心。',
    'services.quality.timing': '7天起',
    'services.samples.title': '样品交付',
    'services.samples.desc': '我们快速发送样品进行质量评估，节省您的时间。',
    'services.samples.timing': '5天起',
    'services.containers.title': '自有集装箱',
    'services.containers.desc': '我们为定期交付提供个人集装箱，优先发货。',
    'services.containers.timing': '7天起',
    'services.manufacturing.title': '合同制造',
    'services.manufacturing.desc': '我们帮助在您的品牌下创建产品：从创意到生产。',
    'services.manufacturing.timing': '14天起',
    'services.marketplaces.title': '进入电商平台',
    'services.marketplaces.desc': '我们指导您完成进入平台的所有阶段：注册，上架，销售。',
    'services.marketplaces.timing': '7-14天起',
    'services.storage.title': '仓库存储',
    'services.storage.desc': '我们在中国和俄罗斯的安全仓库中提供您的货物存储。',
    'services.storage.timing': '1天起',
    'services.competitors.title': '竞争对手分析',
    'services.competitors.desc': '我们展示您的竞争对手如何运输货物 — 从物流到海关方案。',
    'services.competitors.timing': '2小时至1天',
    'services.cta': '申请报价',

    // FAQ section
    'faq.title': '常见问题',
    'faq.delivery.question': '如何组织从中国的货物运输？',
    'faq.delivery.answer': '通过公路运输以三种主要方式进行：\n\n快速汽车 — 交付时间7-8天\n快递服务 — 交付时间3天\n\n我们根据您在时间和预算方面的优先事项选择最佳交付方式。我们负责所有物流阶段 — 从接收货物到抵达俄罗斯。',
    'faq.forwarding.question': '什么是从中国的货运代理？',
    'faq.forwarding.answer': '中国的货运代理是一系列旨在确保货物安全和及时装运的活动。它包括：\n\n• 检查货物的质量和完整性，\n• 照片和视频记录，\n• 控制货物与订单的符合性，\n• 包装和标签，\n• 批次合并，\n• 与供应商和仓库的协调。\n\n货运代理最大限度地减少交付风险，并确保产品在装运前符合期望。',
    'faq.destination.question': '从中国的货物到达哪里？',
    'faq.destination.answer': '默认情况下，货物到达莫斯科的"南门"仓库。经协商，可以交付到"柳布利诺"或"萨多沃德"仓库。\n\n如果您订购5吨或30立方米以上的批次，莫斯科境内的交付是免费的。',
    'faq.regions.question': '如何将货物交付到俄罗斯各地区？',
    'faq.regions.answer': '货物首先到达我们在莫斯科的仓库，然后经客户同意，转交给运输公司，以便进一步交付到俄罗斯各地区。\n交付成本按照所选运输公司的费率计算。',
    'faq.commission.question': '产品购买和搜索的佣金是多少？',
    'faq.commission.answer': '佣金取决于任务的复杂性和请求的数量：\n\n• 搜索和购买商品 — 免费。\n• 如果已经找到产品，只需要向供应商付款 — 服务是免费的。\n• 搜索工厂联系方式 — 固定成本200元。\n\n我们始终提供透明的条件，并在开始工作前讨论成本。',
    'faq.packaging.question': '包装费用是多少？',
    'faq.packaging.answer': '包装根据产品类型、脆弱程度和交付路线选择。以下是主要选项和成本：\n\n• 袋子+胶带 $5\n• 角+袋子+胶带 $6\n• 带子+袋子+胶带 $7\n• 木箱+袋子+胶带 $10\n• 托盘 $45每立方米\n\n为什么包装很重要：在运输过程中，货物可能会被淋湿，被重箱压住或受损。因此，正确选择包装是保护您的产品免受途中任何风险的方法。',
    'faq.cost.question': '运送到俄罗斯的费用是多少？',
    'faq.cost.answer': '交付成本取决于货物类别、密度和所选交付类型，因此没有明确说明就无法给出确切价格。\n\n在👉价格页面上，我们展示了2024年一个流行类别（服装）的大致交付成本。这将帮助您了解价格范围。\n\n要了解您的货物的当前价格 — 提交请求，我们的经理将联系您，明确订单参数，并在5分钟内提供个人商业报价。',
    'faq.prices': '价格',
    'faq.request': '提交请求',

    // Contact form
    'contact.title': '获取费用估算',
    'contact.subtitle': '填写表格，我们将联系您讨论详情',
    'form.product': '产品链接或描述',
    'form.product.placeholder': '指定产品链接或描述您要寻找的内容',
    'form.comment': '评论',
    'form.comment.placeholder': '附加信息或愿望',
    'form.name': '姓名 *',
    'form.name.placeholder': '张三',
    'form.phone': '电话 *',
    'form.phone.placeholder': '+7 (___) ___-__-__',
    'form.submit': '提交请求',
    'form.submitting': '提交中...',
    'form.success': '谢谢！您的请求已发送。我们将尽快与您联系。',
    'form.error.name': '请输入您的姓名',
    'form.error.phone': '请输入您的电话号码',
    'form.error.phone.invalid': '请输入有效的电话号码',

    // Footer
    'footer.company': '您在组织从中国交付的可靠合作伙伴。为企业和个人提供全面解决方案。',
    'footer.services': '服务',
    'footer.prices': '价格',
    'footer.search': '供应商搜索',
    'footer.logistics': '物流',
    'footer.forwarding': '货运代理',
    'footer.payment': '供应商付款',
    'footer.company.title': '公司',
    'footer.about': '关于我们',
    'footer.contacts': '联系方式',
    'footer.contacts.title': '联系方式',
    'footer.rights': '© 2025 6069 Cargo. 版权所有',
    'footer.privacy': '隐私政策',
    'footer.terms': '使用条款'
  }
};

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguage] = useState<Language>(() => {
    // Try to get language from localStorage
    const savedLanguage = localStorage.getItem('language') as Language;
    if (savedLanguage && ['ru', 'en', 'zh'].includes(savedLanguage)) {
      return savedLanguage;
    }
    
    // Check if browser language is available
    const browserLanguage = navigator.language.split('-')[0];
    
    // Return browser language if it's supported, or default to Russian
    if (browserLanguage === 'en') return 'en';
    if (browserLanguage === 'zh') return 'zh';
    return 'ru';
  });

  useEffect(() => {
    // Save language preference to localStorage
    localStorage.setItem('language', language);
    
    // Update html lang attribute
    document.documentElement.lang = language;
  }, [language]);

  const t = (key: string): string => {
    return translations[language][key] || key;
  };

  const value = {
    language,
    setLanguage,
    t
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};